/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2022
 *
 *---------------------------------------------------
 */
//Standard libraries
import React, { createContext, useContext, useEffect, useReducer } from "react";
import { useSelector } from "react-redux";
import { localStorageUtil } from "../../_foundation/utils/storageUtil";
import { STORELOCATORACTIONS, SELECTED_STORE } from "../constants/common";
import { storesListSelector } from "../../redux/selectors/stores";
import { setUserZipcode } from "../../utils/userUtil";

export interface StoreLocatorStateProp {
  selectedStore?: any;
}

/**
 * StoreLocator init state.
 */
const storeLocatorState: StoreLocatorStateProp = {
  selectedStore: null,
};
/**
 * Reducer to be used in StoreLocator.
 * @param state current state of StoreLocator.
 * @param action reducer action.
 * @returns StoreLocator state.
 */
const StoreLocatorReducer = (state: StoreLocatorStateProp, action: any): any => {
  switch (action.type) {
    case STORELOCATORACTIONS.INIT_STORE_SELECTION: {
      const { selectedStore } = action.payload;
      if (selectedStore) {
        localStorageUtil.set(SELECTED_STORE, selectedStore);
        return Object.assign(
          {},
          { ...state },
          {
            selectedStore,
          }
        );
      }
      return state;
    }

    case STORELOCATORACTIONS.UPDATE_STORE_SELECTION: {
      const { selectedStore, postalCode } = action.payload;
      if (selectedStore) {
        localStorageUtil.set(SELECTED_STORE, selectedStore);

        const regex = /^\d{5}$/; // will match string of length 5 containing only digits
        if (postalCode && regex.test(postalCode)) {
          setUserZipcode(postalCode);
        }

        return Object.assign(
          {},
          { ...state },
          {
            selectedStore,
          }
        );
      }
      return state;
    }

    case STORELOCATORACTIONS.RESET_STORE_SELECTOR: {
      localStorageUtil.remove(SELECTED_STORE);
      if (state && state.selectedStore) {
        state.selectedStore = null;
      }
      return state;
    }

    default:
      throw new Error(`Action is not supported: ${action.type}`);
  }
};
/**
 * Context holding StoreLocator information.
 */
export const StoreLocatorContext: React.Context<any> = createContext({});

const initializer = (initstate: StoreLocatorStateProp) => {
  // const selectedStore = localStorageUtil.get(SELECTED_STORE);
  const storeName = process.env.REACT_APP_STORENAME || "";
  const store = localStorage.getItem(`HCS-${storeName}-${SELECTED_STORE}`);
  if (store) {
    const selectedStore = JSON.parse(store);
    const ss = { ...initstate };
    if (selectedStore) {
      ss.selectedStore = selectedStore;
    }
    return ss;
  }
};
/**
 * StoreLocator Context provider.
 */
export const StoreLocatorProvider = ({ children }: any) => {
  const [storeLocator, dispatch] = useReducer(StoreLocatorReducer, storeLocatorState, initializer);
  const value = React.useMemo(() => ({ storeLocator, dispatch }), [storeLocator]);

  //////// TO UPDATE STORE DETAILS IN LOCALSTORAGE TO KEEP DATA IN IT IN SYNC WITH API RESPONSE ////////
  const currSelectedStoreId = storeLocator?.selectedStore?.uniqueID || null;
  const storesList = useSelector(storesListSelector);
  useEffect(() => {
    if (storesList && storesList?.length && currSelectedStoreId) {
      const currStoreFromFetchedResponse = storesList?.find((store) => store?.uniqueID === currSelectedStoreId);
      dispatch({
        type: STORELOCATORACTIONS.UPDATE_STORE_SELECTION,
        payload: { selectedStore: currStoreFromFetchedResponse },
      });
    }
  }, [storesList, currSelectedStoreId]);
  ////////////////////////////////

  return <StoreLocatorContext.Provider value={value}>{children}</StoreLocatorContext.Provider>;
};
type StoreLocatorContextValue = {
  storeLocator: StoreLocatorStateProp;
  dispatch: React.Dispatch<any>;
};
/**
 * Hook return StoreLocator value and reducer dispatcher.
 */
export const useStoreLocatorValue = () => useContext<StoreLocatorContextValue>(StoreLocatorContext);
